import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { UtilisateurService } from 'src/services/utilisateur.service';
import { DateFormatPipe } from "../../../pipes/date-format.pipe";
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ParcoursFormationPilotageModel } from 'src/models/pilotage/parcoursFormationPilotageModel';
import { ParcoursFormationService } from 'src/services/parcours-formation.service';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator, PageEvent, MatPaginatorIntl } from '@angular/material/paginator';
import { MatSortModule, MatSort, Sort } from '@angular/material/sort';
import { MatSelectModule } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CustomPaginatorIntlService } from 'src/services/custom-paginator-intl.service';
import { DeleteParcoursFormationDialogComponent } from './delete-parcours-formation-dialog/delete-parcours-formation-dialog.component';
import { ConfirmationExportDialogComponent } from '../../../commun/dialog/confirmation-export-dialog/confirmation-export-dialog.component';
import { StatePersistenceService } from 'src/services/state-persistence.service';
import { ParcoursFormationManagmentState, defaultParcoursFormationManagmentState, validateParcoursFormationManagmentState } from 'src/models/parcours-formation-managment-state.model';

@Component({
    selector: 'app-parcours-formation-managment',
    imports: [
        CommonModule,
        MatProgressSpinnerModule,
        TranslateModule,
        MatIconModule,
        FormsModule,
        ReactiveFormsModule,
        DateFormatPipe,
        MatTableModule,
        MatPaginatorModule,
        MatSortModule,
        MatSelectModule,
        NgxMatSelectSearchModule
    ],
    templateUrl: './parcours-formation-managment.component.html',
    styleUrl: './parcours-formation-managment.component.scss',
    providers: [{ provide: MatPaginatorIntl, useClass: CustomPaginatorIntlService }]
})
export class ParcoursFormationManagmentComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  parcoursFormations: ParcoursFormationPilotageModel[] = [];
  filteredParcoursFormations: ParcoursFormationPilotageModel[] = [];
  isLoading: boolean = false;
  searchTerm: string = '';
  dataSource = new MatTableDataSource<ParcoursFormationPilotageModel>([]);
  displayedColumns: string[] = ['nom', 'totalModules', 'dateCreation', 'actions'];

  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions: number[] = [5, 10, 25, 50];
  totalItems = 0;

  searchFilterCtrl: FormControl = new FormControl();
  protected _onDestroy = new Subject<void>();

  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' | '' = '';

  private readonly COMPONENT_KEY = 'parcours-formation-managment';

  constructor(
    public parcoursService: ParcoursFormationService,
    private utilisateurService: UtilisateurService,
    public translate: TranslateService,
    private router: Router,
    private dialog: MatDialog,
    private statePersistence: StatePersistenceService
  ) {
    translate.setDefaultLang('fr');
  }

  ngOnInit(): void {
    this.loadSavedState();
    this.loadParcoursFormations();

    this.searchFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.applyFilters();
      });
  }

  ngOnDestroy() {
    this.saveCurrentState();
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  toggleMenu(parcours: ParcoursFormationPilotageModel): void {
    this.filteredParcoursFormations.forEach((p) => {
      if (p.parcoursFormationId === parcours.parcoursFormationId) {
        p.isMenuOpen = !p.isMenuOpen;
      } else {
        p.isMenuOpen = false;
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const isInsideMenu = target.closest('.menu-container');
    if (!isInsideMenu) {
      this.filteredParcoursFormations.forEach(p => p.isMenuOpen = false);
    }
  }

  paginer(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.applyFilters();
    this.saveCurrentState();
  }

  private loadParcoursFormations(): void {
    this.isLoading = true;

    this.parcoursService.obtenirParcoursFormations().subscribe(
      (res) => {
        this.parcoursFormations = res;
        this.filteredParcoursFormations = [...this.parcoursFormations];
        this.updateDataSource();
        this.isLoading = false;
      },
      (error) => {
        console.error('Error loading parcours formations:', error);
        this.isLoading = false;
      }
    );
  }

  updateDataSource() {
    this.dataSource.data = this.filteredParcoursFormations;
    this.totalItems = this.filteredParcoursFormations.length;
    setTimeout(() => {
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
    });
  }

  applyFilters(): void {
    if (!this.searchTerm) {
      this.filteredParcoursFormations = [...this.parcoursFormations];
    } else {
      const search = this.searchTerm.toLowerCase();
      this.filteredParcoursFormations = this.parcoursFormations.filter(parcours =>
        this.parcoursService.getNomLocaliseFromLocales(parcours.locales).toLowerCase().includes(search)
      );
    }

    if (this.sortColumn && this.sortDirection) {
      this.filteredParcoursFormations.sort((a, b) => {
        let valueA: any, valueB: any;
        if (this.sortColumn === 'nom') {
          valueA = this.parcoursService.getNomLocaliseFromLocales(a.locales).toLowerCase();
          valueB = this.parcoursService.getNomLocaliseFromLocales(b.locales).toLowerCase();
        } else if (this.sortColumn === 'totalModules') {
          valueA = a.totalPackages;
          valueB = b.totalPackages;
        } else if (this.sortColumn === 'dateCreation') {
          valueA = new Date(a.dateCreation);
          valueB = new Date(b.dateCreation);
        }
        if (valueA < valueB) return this.sortDirection === 'asc' ? -1 : 1;
        if (valueA > valueB) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    this.updateDataSource();
  }

  reinitialiserFiltre(): void {
    this.searchTerm = '';
    this.searchFilterCtrl.reset();
    this.applyFilters();
  }

  sortDataChange(sort: Sort) {
    this.sortColumn = sort.active;
    this.sortDirection = sort.direction as 'asc' | 'desc' | '';
    this.applyFilters();
  }

  editParcours(parcours: ParcoursFormationPilotageModel): void {
    this.router.navigate(['/parcours-formation/edit', parcours.parcoursFormationId]);
  }

  createParcours(): void {
    this.statePersistence.clearState(this.COMPONENT_KEY);
    this.router.navigate(['/parcours-formation/create']);
  }

  deleteParcours(parcours: ParcoursFormationPilotageModel): void {
    const dialogRef = this.dialog.open(DeleteParcoursFormationDialogComponent, {
      width: '500px',
      data: { parcoursFormationId: parcours.parcoursFormationId }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadParcoursFormations();
      }
    });
  }

  exporterParcours(parcours: ParcoursFormationPilotageModel): void {
    const dialogRef = this.dialog.open(ConfirmationExportDialogComponent, {
      width: '600px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isLoading = true;
        const codeLangue = this.translate.currentLang || 'fr';
        this.parcoursService.exporterParcoursFormation(parcours.parcoursFormationId, codeLangue).subscribe(blob => {
          const now = new Date();
          const pad = (n: number) => n.toString().padStart(2, '0');
          const datetime = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
          const filename = `parcours_${datetime}.xlsx`;
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          link.click();
          window.URL.revokeObjectURL(link.href);
          this.isLoading = false;
        }, () => {
          this.isLoading = false;
        });
      }
    });
  }

  private loadSavedState(): void {
    const savedState = this.statePersistence.loadState(this.COMPONENT_KEY);
    if (savedState) {
      const validatedState = validateParcoursFormationManagmentState(savedState);
      this.searchTerm = validatedState.searchTerm;
      this.pageIndex = validatedState.pageIndex;
      this.pageSize = validatedState.pageSize;
      this.searchFilterCtrl.setValue(this.searchTerm, { emitEvent: false });
    }
  }

  private saveCurrentState(): void {
    const state = {
      searchTerm: this.searchTerm,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize
    };
    this.statePersistence.saveState(this.COMPONENT_KEY, state);
  }


}
